export interface TextPresetConfig {
  id: string
  name: string
  category: TextPresetCategory
  exampleText: string
  exampleSubtitle?: string
  fontFamily: string
  fontSize: number
  fontWeight: number
  color: string
  position: string
  animation: string
  animationDuration?: number
  enterAnimation?: string
  exitAnimation?: string
  emphasisAnimation?: string
  enterDuration?: number
  exitDuration?: number
  background?: string
  letterSpacing?: string
  textTransform?: 'uppercase' | 'lowercase' | 'none'
  fontStyle?: 'italic' | 'normal'
  textStroke?: string
  glowColor?: string
  shadow?: string
  accentBar?: { side: 'left' | 'top' | 'bottom'; color: string; width: number }
  lineAbove?: boolean
  lineBelow?: boolean
  subtitle?: { fontSize: number; fontWeight: number; fontFamily?: string; color?: string; letterSpacing?: string }
  padding?: string
  borderRadius?: number
  maxWidth?: string
}

export type TextPresetCategory =
  | 'titles'
  | 'lower_thirds'
  | 'labels'
  | 'callouts'
  | 'bold'
  | 'minimal'

export const PRESET_CATEGORY_LABELS: Record<TextPresetCategory, string> = {
  titles: 'TITLES',
  lower_thirds: 'LOWER THIRDS',
  labels: 'LABELS & BADGES',
  callouts: 'CALLOUTS & QUOTES',
  bold: 'BOLD / IMPACT',
  minimal: 'MINIMAL',
}

export const PRESET_FONTS = [
  'Bricolage Grotesque',
  'Cinzel',
  'Playfair Display',
  'Libre Bodoni',
  'Cormorant Garamond',
  'Abril Fatface',
  'DM Serif Display',
  'Crimson Text',
  'Bebas Neue',
  'Montserrat',
  'Outfit',
  'Inter',
  'Barlow Condensed',
  'Oswald',
  'Orbitron',
  'Righteous',
  'JetBrains Mono',
] as const

export const FONT_CSS_MAP: Record<string, string> = {
  'Inter': 'Inter, sans-serif',
  'Outfit': 'Outfit, sans-serif',
  'Playfair Display': '"Playfair Display", serif',
  'Cormorant Garamond': '"Cormorant Garamond", serif',
  'JetBrains Mono': '"JetBrains Mono", monospace',
  'Oswald': 'Oswald, sans-serif',
  'Bebas Neue': '"Bebas Neue", sans-serif',
  'DM Serif Display': '"DM Serif Display", serif',
  'Space Mono': '"Space Mono", monospace',
  'Crimson Text': '"Crimson Text", serif',
  'Cinzel': 'Cinzel, serif',
  'Bricolage Grotesque': '"Bricolage Grotesque", sans-serif',
  'Libre Bodoni': '"Libre Bodoni", serif',
  'Abril Fatface': '"Abril Fatface", serif',
  'Montserrat': 'Montserrat, sans-serif',
  'Orbitron': 'Orbitron, sans-serif',
  'Barlow Condensed': '"Barlow Condensed", sans-serif',
  'Righteous': 'Righteous, sans-serif',
}

export function resolveFontCSS(name?: string): string {
  if (!name) return FONT_CSS_MAP['Inter']
  return FONT_CSS_MAP[name] || `"${name}", sans-serif`
}

/**
 * The default text shadow for titles with no explicit shadow.
 *
 * Layered, not flat: a crisp 1px dark halo for edge separation on any footage,
 * a tight contact shadow, a soft mid shadow, and a wide ambient bloom. This is
 * what reads as "cinematic depth" and keeps white text legible over bright or
 * busy frames — replacing the old flat `0 2px 1px rgba(0,0,0,0.6)`.
 */
export const CINEMATIC_TEXT_SHADOW =
  '0 0 1px rgba(0,0,0,0.55), 0 1px 2px rgba(0,0,0,0.5), 0 4px 18px rgba(0,0,0,0.5), 0 14px 50px rgba(0,0,0,0.4)'

/** Heavier, punchy shadow for bold YouTube-style display type. */
export const BOLD_TEXT_SHADOW =
  '0 2px 0 rgba(0,0,0,0.45), 0 4px 14px rgba(0,0,0,0.6), 0 10px 40px rgba(0,0,0,0.5)'

export const TEXT_PRESETS: TextPresetConfig[] = [
  // ─── TITLES ──────────────────────────────────────────
  // The flagship default. Bricolage Grotesque at heavy weight — a thick
  // contemporary grotesque with genuine personality (lively, slightly irregular
  // letterforms) — in confident uppercase. Punchy enough for short statements,
  // clean enough for longer titles. Defaults to centered, but the agent can
  // anchor it lower-left (often with a kicker) for longer titles or an editorial
  // feel; maxWidth keeps long lines from spanning edge to edge. This is what
  // `cinematic_title` resolves to.
  {
    id: 'cinematic_title',
    name: 'Cinematic',
    category: 'titles',
    exampleText: 'CINEMATIC',
    fontFamily: 'Bricolage Grotesque',
    fontSize: 70,
    fontWeight: 800,
    color: '#f5f3ee',
    position: 'center',
    animation: 'slide_up',
    enterAnimation: 'slide_up',
    exitAnimation: 'fade',
    enterDuration: 0.7,
    exitDuration: 0.6,
    letterSpacing: '-0.005em',
    textTransform: 'uppercase',
    maxWidth: '80%',
    shadow: CINEMATIC_TEXT_SHADOW,
  },
  // Italic serif, graceful. Soft de-blur entrance, warm off-white. Romantic,
  // editorial, documentary-intro energy.
  {
    id: 'elegant_title',
    name: 'Elegant',
    category: 'titles',
    exampleText: 'Elegant',
    fontFamily: 'Playfair Display',
    fontSize: 66,
    fontWeight: 500,
    color: '#f2ede4',
    position: 'center',
    animation: 'blur_in',
    enterAnimation: 'blur_in',
    exitAnimation: 'fade',
    enterDuration: 1.4,
    exitDuration: 0.9,
    letterSpacing: '0.03em',
    fontStyle: 'italic',
    shadow: CINEMATIC_TEXT_SHADOW,
  },
  // Clean modern sans, wide uppercase tracking, weighted rise. The versatile,
  // neutral-but-refined workhorse title.
  {
    id: 'standard_title',
    name: 'Standard',
    category: 'titles',
    exampleText: 'STANDARD',
    fontFamily: 'Montserrat',
    fontSize: 54,
    fontWeight: 600,
    color: '#ffffff',
    position: 'center',
    animation: 'slide_up',
    enterAnimation: 'slide_up',
    exitAnimation: 'fade',
    enterDuration: 0.9,
    exitDuration: 0.7,
    letterSpacing: '0.24em',
    textTransform: 'uppercase',
    shadow: CINEMATIC_TEXT_SHADOW,
  },
  // Towering condensed display, a slow weighted push-in. Trailer / blockbuster
  // hero card — use sparingly for big moments.
  {
    id: 'hero_title',
    name: 'Hero',
    category: 'titles',
    exampleText: 'HERO',
    fontFamily: 'Bebas Neue',
    fontSize: 116,
    fontWeight: 400,
    color: '#ffffff',
    position: 'center',
    animation: 'scale',
    enterAnimation: 'scale',
    exitAnimation: 'fade',
    enterDuration: 1.0,
    exitDuration: 0.8,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    shadow: CINEMATIC_TEXT_SHADOW,
  },
  // Editorial bold with a cool glow and continuous shimmer. High-energy,
  // title-sequence / music-video feel.
  {
    id: 'neon_title',
    name: 'Neon',
    category: 'titles',
    exampleText: 'NEON',
    fontFamily: 'Orbitron',
    fontSize: 56,
    fontWeight: 700,
    color: '#eaf4ff',
    position: 'center',
    animation: 'fade',
    enterAnimation: 'fade',
    exitAnimation: 'blur_out',
    emphasisAnimation: 'glow_pulse',
    enterDuration: 1.1,
    exitDuration: 0.8,
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    glowColor: 'rgba(96,170,255,0.45)',
  },
  // Chapter divider — slim rules above and below, a quiet fade-and-settle.
  {
    id: 'chapter_title',
    name: 'Chapter Card',
    category: 'titles',
    exampleText: 'Chapter One',
    fontFamily: 'Cormorant Garamond',
    fontSize: 56,
    fontWeight: 400,
    color: 'rgba(245,243,238,0.92)',
    position: 'center',
    animation: 'fade',
    enterAnimation: 'fade',
    exitAnimation: 'fade',
    enterDuration: 1.3,
    exitDuration: 0.9,
    letterSpacing: '0.08em',
    lineAbove: true,
    lineBelow: true,
    shadow: CINEMATIC_TEXT_SHADOW,
  },
  // Explicit YouTube / creator style: heavy sans, thick dark outline + chunky
  // shadow for legibility over anything, a punchy pop entrance.
  {
    id: 'youtube_bold',
    name: 'YouTube Bold',
    category: 'titles',
    exampleText: 'WATCH THIS',
    fontFamily: 'Montserrat',
    fontSize: 80,
    fontWeight: 800,
    color: '#ffffff',
    position: 'center',
    animation: 'bounce',
    enterAnimation: 'bounce',
    exitAnimation: 'scale',
    enterDuration: 0.55,
    exitDuration: 0.5,
    letterSpacing: '0',
    textTransform: 'uppercase',
    textStroke: '3px rgba(0,0,0,0.92)',
    shadow: BOLD_TEXT_SHADOW,
  },
  // Noir — italic Bodoni, low-key, drifts in out of focus.
  {
    id: 'film_noir',
    name: 'Film Noir',
    category: 'titles',
    exampleText: 'Film Noir',
    fontFamily: 'Libre Bodoni',
    fontSize: 66,
    fontWeight: 400,
    color: '#ece4d6',
    position: 'center',
    animation: 'blur_in',
    enterAnimation: 'blur_in',
    exitAnimation: 'blur_out',
    enterDuration: 1.5,
    exitDuration: 1.0,
    letterSpacing: '0.05em',
    fontStyle: 'italic',
    shadow: CINEMATIC_TEXT_SHADOW,
  },
  // Vintage poster — fat display serif, warm paper tone, a confident scale-in.
  {
    id: 'vintage_poster',
    name: 'Vintage Poster',
    category: 'titles',
    exampleText: 'Vintage',
    fontFamily: 'Abril Fatface',
    fontSize: 78,
    fontWeight: 400,
    color: '#ece1cf',
    position: 'center',
    animation: 'scale',
    enterAnimation: 'scale',
    exitAnimation: 'fade',
    enterDuration: 1.1,
    exitDuration: 0.8,
    letterSpacing: '0.01em',
    shadow: CINEMATIC_TEXT_SHADOW,
  },
  // Action — tight condensed, hard fast rise. Sports / thriller / kinetic.
  {
    id: 'action_title',
    name: 'Action',
    category: 'titles',
    exampleText: 'PURSUIT',
    fontFamily: 'Barlow Condensed',
    fontSize: 84,
    fontWeight: 800,
    color: '#ffffff',
    position: 'center',
    animation: 'slide_up',
    enterAnimation: 'slide_up',
    exitAnimation: 'slide_up',
    enterDuration: 0.5,
    exitDuration: 0.4,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    shadow: BOLD_TEXT_SHADOW,
  },

  // ─── LOWER THIRDS ───────────────────────────────────
  {
    id: 'clean_lower_third',
    name: 'Clean Lower Third',
    category: 'lower_thirds',
    exampleText: 'Speaker Name',
    exampleSubtitle: 'Title or Description',
    fontFamily: 'Outfit',
    fontSize: 34,
    fontWeight: 600,
    color: '#ffffff',
    position: 'lower_third',
    animation: 'slide_right',
    animationDuration: 0.4,
    accentBar: { side: 'left', color: '#3b82f6', width: 3 },
    subtitle: { fontSize: 20, fontWeight: 400, fontFamily: 'Outfit', color: '#b0b0b0' },
  },
  {
    id: 'banner_lower_third',
    name: 'Banner',
    category: 'lower_thirds',
    exampleText: 'Full Width Banner Text',
    fontFamily: 'Outfit',
    fontSize: 30,
    fontWeight: 500,
    color: '#ffffff',
    position: 'bottom_center',
    animation: 'slide_up',
    animationDuration: 0.35,
    background: 'rgba(0,0,0,0.7)',
    padding: '14px 32px',
    maxWidth: '100%',
  },
  {
    id: 'accent_lower_third',
    name: 'Accent Bar',
    category: 'lower_thirds',
    exampleText: 'John Doe',
    exampleSubtitle: 'Creative Director',
    fontFamily: 'Outfit',
    fontSize: 28,
    fontWeight: 600,
    color: '#ffffff',
    position: 'lower_third',
    animation: 'slide_right',
    animationDuration: 0.35,
    accentBar: { side: 'left', color: '#e5e5e5', width: 2 },
    background: 'rgba(0,0,0,0.55)',
    padding: '12px 20px',
    borderRadius: 4,
    subtitle: { fontSize: 17, fontWeight: 400, fontFamily: 'Outfit', color: '#a0a0a0' },
  },
  {
    id: 'minimal_lower_third',
    name: 'Minimal Lower Third',
    category: 'lower_thirds',
    exampleText: 'Jane Smith',
    fontFamily: 'Inter',
    fontSize: 26,
    fontWeight: 500,
    color: '#ffffff',
    position: 'lower_third',
    animation: 'fade',
    animationDuration: 0.6,
    letterSpacing: '0.02em',
  },
  {
    id: 'gradient_lower_third',
    name: 'Gradient Strip',
    category: 'lower_thirds',
    exampleText: 'Featured Story',
    exampleSubtitle: 'A closer look',
    fontFamily: 'Outfit',
    fontSize: 30,
    fontWeight: 500,
    color: '#ffffff',
    position: 'lower_third',
    animation: 'slide_right',
    animationDuration: 0.4,
    background: 'linear-gradient(90deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 100%)',
    padding: '10px 24px',
    letterSpacing: '0.03em',
    subtitle: { fontSize: 17, fontWeight: 300, fontFamily: 'Outfit', letterSpacing: '0.02em', color: '#c0c0c0' },
  },
  {
    id: 'documentary_lower_third',
    name: 'Documentary',
    category: 'lower_thirds',
    exampleText: 'Dr. Sarah Chen',
    exampleSubtitle: 'Marine Biologist',
    fontFamily: 'Montserrat',
    fontSize: 28,
    fontWeight: 600,
    color: '#ffffff',
    position: 'lower_third',
    animation: 'fade',
    animationDuration: 0.5,
    letterSpacing: '0.02em',
    lineBelow: true,
    subtitle: { fontSize: 18, fontWeight: 400, fontFamily: 'Montserrat', color: '#b0b0b0', letterSpacing: '0.06em' },
  },

  // ─── LABELS & BADGES ────────────────────────────────
  {
    id: 'top_badge',
    name: 'Top Badge',
    category: 'labels',
    exampleText: 'LIVE',
    fontFamily: 'Outfit',
    fontSize: 16,
    fontWeight: 700,
    color: '#ffffff',
    position: 'top_center',
    animation: 'fade',
    animationDuration: 0.3,
    background: '#dc2626',
    padding: '5px 16px',
    borderRadius: 3,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  {
    id: 'corner_label',
    name: 'Corner Label',
    category: 'labels',
    exampleText: 'NEW',
    fontFamily: 'Outfit',
    fontSize: 14,
    fontWeight: 700,
    color: '#ffffff',
    position: 'upper_third',
    animation: 'fade',
    animationDuration: 0.3,
    background: 'rgba(255,255,255,0.15)',
    padding: '4px 12px',
    borderRadius: 2,
    textTransform: 'uppercase',
    letterSpacing: '0.12em',
  },
  {
    id: 'bottom_tag',
    name: 'Bottom Tag',
    category: 'labels',
    exampleText: '01:23:45',
    fontFamily: 'JetBrains Mono',
    fontSize: 15,
    fontWeight: 400,
    color: '#a0a0a0',
    position: 'bottom_center',
    animation: 'fade',
    animationDuration: 0.3,
    background: 'rgba(0,0,0,0.5)',
    padding: '4px 12px',
    borderRadius: 3,
  },

  // ─── CALLOUTS & QUOTES ──────────────────────────────
  {
    id: 'left_callout',
    name: 'Left Callout',
    category: 'callouts',
    exampleText: 'Something worth\nremembering.',
    fontFamily: 'Libre Bodoni',
    fontSize: 44,
    fontWeight: 400,
    color: '#f5f0e8',
    position: 'center',
    animation: 'fade',
    animationDuration: 0.8,
    fontStyle: 'italic',
    accentBar: { side: 'left', color: 'rgba(255,255,255,0.3)', width: 2 },
  },
  {
    id: 'center_quote',
    name: 'Center Quote',
    category: 'callouts',
    exampleText: 'Words can inspire.',
    fontFamily: 'Playfair Display',
    fontSize: 42,
    fontWeight: 500,
    color: '#ffffff',
    position: 'center',
    animation: 'fade',
    animationDuration: 0.8,
    lineAbove: true,
    lineBelow: true,
  },
  {
    id: 'right_pullout',
    name: 'Right Pullout',
    category: 'callouts',
    exampleText: 'Bold perspective.',
    fontFamily: 'Montserrat',
    fontSize: 36,
    fontWeight: 600,
    color: '#e8e8e8',
    position: 'center',
    animation: 'slide_left',
    animationDuration: 0.5,
    letterSpacing: '0.03em',
  },

  // ─── BOLD / IMPACT ──────────────────────────────────
  {
    id: 'breaking',
    name: 'Headline',
    category: 'bold',
    exampleText: 'BREAKING',
    fontFamily: 'Barlow Condensed',
    fontSize: 58,
    fontWeight: 800,
    color: '#ffffff',
    position: 'center',
    animation: 'slide_up',
    animationDuration: 0.4,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    lineBelow: true,
  },
  {
    id: 'statement',
    name: 'Statement',
    category: 'bold',
    exampleText: 'MAKE IT\nHAPPEN',
    fontFamily: 'Bebas Neue',
    fontSize: 76,
    fontWeight: 400,
    color: '#ffffff',
    position: 'center',
    animation: 'fade',
    animationDuration: 0.8,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  {
    id: 'dramatic',
    name: 'Dramatic',
    category: 'bold',
    exampleText: 'DRAMATIC',
    fontFamily: 'Cinzel',
    fontSize: 68,
    fontWeight: 900,
    color: '#ffffff',
    position: 'center',
    animation: 'zoom_in',
    animationDuration: 0.7,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    shadow: '0 4px 24px rgba(0,0,0,0.8)',
  },

  // ─── MINIMAL ────────────────────────────────────────
  {
    id: 'simple',
    name: 'Simple',
    category: 'minimal',
    exampleText: 'Simple clean text',
    fontFamily: 'Inter',
    fontSize: 28,
    fontWeight: 400,
    color: '#e0e0e0',
    position: 'bottom_center',
    animation: 'none',
  },
  {
    id: 'typewriter_text',
    name: 'Typewriter',
    category: 'minimal',
    exampleText: 'typing effect...',
    fontFamily: 'JetBrains Mono',
    fontSize: 24,
    fontWeight: 400,
    color: '#e8dcc8',
    position: 'center',
    animation: 'typewriter',
    animationDuration: 0.8,
  },
  {
    id: 'light_sans',
    name: 'Light Sans',
    category: 'minimal',
    exampleText: 'whisper soft',
    fontFamily: 'Outfit',
    fontSize: 30,
    fontWeight: 300,
    color: 'rgba(255,255,255,0.75)',
    position: 'bottom_center',
    animation: 'fade',
    animationDuration: 1.0,
    letterSpacing: '0.06em',
  },
]

export function getPresetsByCategory(): Record<TextPresetCategory, TextPresetConfig[]> {
  const grouped: Record<TextPresetCategory, TextPresetConfig[]> = {
    titles: [],
    lower_thirds: [],
    labels: [],
    callouts: [],
    bold: [],
    minimal: [],
  }
  for (const preset of TEXT_PRESETS) {
    grouped[preset.category].push(preset)
  }
  return grouped
}

/**
 * Legacy preset IDs → current catalog IDs. Older saved overlays (and any
 * external references) keep rendering after the title catalog was reorganized
 * around the agent's vocabulary. Additive and backward-compatible — never
 * remove an entry here.
 */
export const PRESET_ALIASES: Record<string, string> = {
  cinema_classic: 'cinematic_title',
  blockbuster_title: 'hero_title',
  prestige_title: 'elegant_title',
  modern_title: 'standard_title',
  scifi_title: 'neon_title',
  art_deco_title: 'vintage_poster',
}

export function getPresetById(id: string): TextPresetConfig | undefined {
  const resolved = PRESET_ALIASES[id] || id
  return TEXT_PRESETS.find(p => p.id === resolved)
}

/** Animation fields, with the overlay's explicit values taking priority over its preset's. */
export interface EffectiveAnimation {
  animation?: string
  enterAnimation?: string
  exitAnimation?: string
  emphasisAnimation?: string
  animationDuration?: number
  enterDuration?: number
  exitDuration?: number
}

/**
 * Resolve the animation an overlay should actually play.
 *
 * The agent typically sets only `preset`, so the preset's enter/exit/emphasis
 * must flow through to the renderer — otherwise preset titles render static.
 * Explicit overlay fields still win (manual overrides from the editor).
 */
export function resolveOverlayAnimation(overlay: {
  preset?: string
  animation?: string
  enterAnimation?: string
  exitAnimation?: string
  emphasisAnimation?: string
  animationDuration?: number
  enterDuration?: number
  exitDuration?: number
}): EffectiveAnimation {
  const preset = overlay.preset ? getPresetById(overlay.preset) : undefined
  return {
    animation: overlay.animation ?? preset?.animation,
    enterAnimation: overlay.enterAnimation ?? preset?.enterAnimation,
    exitAnimation: overlay.exitAnimation ?? preset?.exitAnimation,
    emphasisAnimation: overlay.emphasisAnimation ?? preset?.emphasisAnimation,
    animationDuration: overlay.animationDuration ?? preset?.animationDuration,
    enterDuration: overlay.enterDuration ?? preset?.enterDuration,
    exitDuration: overlay.exitDuration ?? preset?.exitDuration,
  }
}

/**
 * The text-shadow an overlay should render with: explicit shadow → preset
 * shadow → the layered cinematic default. (The renderers previously dropped
 * the preset shadow entirely and fell back to a flat one.)
 */
export function resolveOverlayTextShadow(
  overlay: { shadow?: string },
  preset?: TextPresetConfig | null,
): string {
  return overlay.shadow ?? preset?.shadow ?? CINEMATIC_TEXT_SHADOW
}

export function presetToOverlayFields(preset: TextPresetConfig) {
  return {
    preset: preset.id,
    fontFamily: preset.fontFamily,
    fontSize: preset.fontSize,
    fontWeight: preset.fontWeight,
    color: preset.color,
    position: preset.position,
    animation: preset.animation,
    animationDuration: preset.animationDuration,
    enterAnimation: preset.enterAnimation,
    exitAnimation: preset.exitAnimation,
    emphasisAnimation: preset.emphasisAnimation,
    enterDuration: preset.enterDuration,
    exitDuration: preset.exitDuration,
    background: preset.background,
    letterSpacing: preset.letterSpacing,
    textTransform: preset.textTransform,
    fontStyle: preset.fontStyle,
    textStroke: preset.textStroke,
    glowColor: preset.glowColor,
    shadow: preset.shadow,
    accentBar: preset.accentBar ? JSON.stringify(preset.accentBar) : undefined,
    lineAbove: preset.lineAbove,
    lineBelow: preset.lineBelow,
    subtitleConfig: preset.subtitle ? JSON.stringify(preset.subtitle) : undefined,
    padding: preset.padding,
    borderRadius: preset.borderRadius,
    maxWidth: preset.maxWidth,
  }
}
